import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbcCore } from '@barthauer/abc/core';
import {
  AuthenticationInterceptor,
  LoginInfo,
  ProfileInfo,
  ProfileService,
  ProfileSettingsService,
  UserService,
} from '@barthauer/abc/core/authentication';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { DataSource, ArrayStore } from 'devextreme/common/data';
//import { HelperService } from 'libs/abc/management/admin/src/lib/services/helper.service';
import { BehaviorSubject, merge, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, mapTo, publishReplay, refCount, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'basys-authentication-profile-selection',
  templateUrl: './profile-selection.component.html',
  styleUrls: ['./profile-selection.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ProfileSelectionComponent implements OnInit, OnDestroy {
  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;
  profilesDataSource: DataSource;
  selectedProfile = { displayText: '', description: '', mapImagePreview: '', id: '' };
  loginInfo$: Observable<LoginInfo>;
  isLoadingProfiles$: Observable<boolean>;
  profiles$: Observable<ProfileInfo[]>;
  isApplyingProfile: boolean;
  errorMessage: string;
  @Input() errorClass: string;
  private readonly destroy$ = new Subject<void>();

  private readonly refreshToken$ = new BehaviorSubject(undefined);

  private ForwardToRoutePath: string;

  private ForceReloadOnApply: boolean;

  constructor(
    private profileService: ProfileService,
    private profileSettingsService: ProfileSettingsService,
    private abcCore: AbcCore,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private resourceService: ResourceManagerService
  ) {
    this.resAsync = this.resourceService.getResourceSet('abc-core-authentication');
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.ForwardToRoutePath = data.ForwardTo;
        this.ForceReloadOnApply = data.ForceReloadOnApply;
      },
      error: (err) => this.abcCore.error.publish(err),
    });

    this.errorMessage = null;
  }

  listSelectionChanged = (e) => {
    this.selectedProfile = e.addedItems[0];
  };

  async ngOnInit() {
    this.res = await this.resAsync;
    this.errorMessage = null;

    this.loginInfo$ = this.userService.getCurrentLoginInfo().pipe(
      catchError((err) => {
        this.errorMessage = this.abcCore.error.getFullErrorMessage(err);
        return of(<LoginInfo>null);
      })
    );

    this.profiles$ = this.refreshToken$.pipe(
      tap((n) => {
        this.errorMessage = null;
      }),
      switchMap((n) =>
        this.profileService.getProfilesForCurrentUser().pipe(
          // delay(5000), // For testing/debugging loading phase
          catchError((err) => {
            this.errorMessage = this.abcCore.error.getFullErrorMessage(err);
            return of(<ProfileInfo[]>null);
          })
        )
      ),
      publishReplay(1),
      refCount()
    );

    this.isLoadingProfiles$ = merge(
      this.refreshToken$.pipe(mapTo(true)),
      this.profiles$.pipe(
        concatMap((profiles) => {
          this.profilesDataSource = new DataSource({
            store: new ArrayStore({
              key: 'displayText',
              data: profiles,
            }),
          });

          if (profiles.length !== 0) {
            this.selectedProfile.displayText = profiles[0].displayText;
            this.selectedProfile.description = profiles[0].description;
            this.selectedProfile.mapImagePreview = profiles[0].mapImagePreview;
            this.selectedProfile.id = profiles[0].id;
          }

          return this.profileSettingsService.getCurrentProfileSettings().pipe(
            tap((val) => {
              if (val) {
                this.selectedProfile.displayText = val.displayText;
                this.selectedProfile.description = val.description;
                this.selectedProfile.mapImagePreview = val.mapImagePreview;
              }
            })
          );
        }),
        // Even errors indicate we're not still loading.
        catchError(() => of(undefined)),
        mapTo(false)
      )
    ).pipe(
      // tap((b) => {
      //   console.log(`IsLoading Subscription: ${b}`);
      // }),
      publishReplay(1),
      refCount()
    );
  }

  getProgressTemplateContext(message: string): any {
    return { message: message };
  }
  ngOnDestroy(): void {
    this.errorMessage = null;
    this.refreshToken$.complete();
    this.destroy$.next(null); // trigger the unsubscribe
    this.destroy$.complete(); // finalize & clean up the subject stream
  }

  logout(removeCurrentProfileFromStorage: boolean = false): void {
    this.errorMessage = null;

    if (removeCurrentProfileFromStorage) {
      this.profileService
        .removeCurrentProfile()
        .pipe(
          concatMap(() => this.userService.logoutCurrentUser(true)),
          takeUntil(this.destroy$)
        )
        .subscribe({ error: (err) => this.abcCore.error.publish(err) });
    } else {
      this.userService
        .logoutCurrentUser(true)
        .pipe(takeUntil(this.destroy$))
        .subscribe({ error: (err) => this.abcCore.error.publish(err) });
    }
  }

  selectProfileAndApply(profileInfo: ProfileInfo): void {
    if (profileInfo) {
      this.isApplyingProfile = true;

      this.profileService
        .setCurrentProfile(profileInfo)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            // Navigate to this.ForwardToRoutePath
            let routePath: string;

            if (this.ForwardToRoutePath) {
              routePath = this.ForwardToRoutePath;
              // } else if (
              //   this.configService &&
              //   this.configService.configuration &&
              //   this.configService.configuration.DefaultRoute
              // ) {
              //   routePath = this.configService.configuration.DefaultRoute;
            } else {
              routePath = '/';
            }
            if (this.ForceReloadOnApply) {
              AuthenticationInterceptor.reload(routePath);
              this.isApplyingProfile = false;
            } else {
              this.router.navigate([routePath]).finally(() => (this.isApplyingProfile = false));
            }
          },
          error: (err) => {
            this.isApplyingProfile = false;
            this.errorMessage = this.abcCore.error.getFullErrorMessage(err);
          },
          complete: () => {},
        });
    }
  }
}
